/* eslint-disable react/function-component-definition */
import { Query } from "@apollo/client/react/components";
import BrazilianCompanyPage from "components/company/v2/BrazilianCompanyPage";
import IssuerByTickerSymbolQuery from "lib/queries/IssuerByTickerSymbolQuery";
import LiquidityByTickerSymbolQuery from "lib/queries/LiquidityByTickerSymbolQuery";
import type {
  IIssuerByTickerSymbolQuery,
  IIssuerByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import type {
  ILiquidityByTickerSymbolQuery,
  ILiquidityByTickerSymbolQueryVariables,
} from "lib/queries/__generated__/LiquidityByTickerSymbolQuery.generated";
import type { NextPage } from "next";
import OverdueSubscriptionStickyBanner from "../../components/billing/OverdueSubscriptionStickyBanner";
import BrazilianCompanyContext from "../../components/company/BrazilianCompanyContext";
import NotFound from "../../components/errors/NotFound";
import LocaleContext from "../../components/i18n/LocaleContext";
import FundamenteiWithTextLayout from "../../components/layouts/FundamenteiWithTextLayout";
import NavbarLayout from "../../components/layouts/NavbarLayout";
import BrazilianRealEstateFund from "../../components/realestatefunds/BrazilianRealEstateFund";
import BrazilianRealEstateFundContext from "../../components/realestatefunds/BrazilianRealEstateFundContext";
import GatedBrazilianRealEstateFund from "../../components/realestatefunds/GatedBrazilianRealEstateFund";
import useSelfie from "../../lib/hooks/useSelfie";
import SetHttpStatusCode from "../../lib/http/SetHttpStatusCode";
import { IAssetNamespace, IQuotePeriod, ISecurityCountry, ITradingSessionPeriod } from "../../types/graphqlTypes";

interface IIssuerByTickerSymbolProps {
  tickerSymbol: string | string[] | undefined;
}

const IssuerByTickerSymbol: NextPage<IIssuerByTickerSymbolProps> = ({ tickerSymbol }) => {
  const user = useSelfie();
  const normalizedTickerSymbol = Array.isArray(tickerSymbol)
    ? tickerSymbol[0].toUpperCase()
    : (tickerSymbol || "").toUpperCase();

  if (!normalizedTickerSymbol || normalizedTickerSymbol.length > 6) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  if (/^([0-9]+)$/.test(normalizedTickerSymbol)) {
    return (
      <>
        <SetHttpStatusCode httpStatus={404} />
        <NotFound />
      </>
    );
  }

  return (
    <>
      <Query<ILiquidityByTickerSymbolQuery, ILiquidityByTickerSymbolQueryVariables>
        query={LiquidityByTickerSymbolQuery}
        variables={{
          namespace: IAssetNamespace.Bra,
          tickerSymbol: `${normalizedTickerSymbol.toUpperCase()}3`,
          tradingSessionsPeriod: ITradingSessionPeriod.P30D,
          quotesPeriod: user && user.isPremium ? IQuotePeriod.P10Y : IQuotePeriod.P5Y,
        }}
      >
        {() => {
          return null;
        }}
      </Query>

      <Query<IIssuerByTickerSymbolQuery, IIssuerByTickerSymbolQueryVariables>
        query={IssuerByTickerSymbolQuery}
        variables={{
          tickerSymbol: normalizedTickerSymbol,
          country: ISecurityCountry.Br,
        }}
      >
        {({ data: currentData, loading, previousData }) => {
          const data = loading && previousData ? previousData : currentData;

          if (!data && loading) {
            return null;
          }

          if (!data) {
            return (
              <>
                <SetHttpStatusCode httpStatus={404} />
                <NotFound />
              </>
            );
          }

          if (!data.issuerByTickerSymbol) {
            return (
              <>
                <SetHttpStatusCode httpStatus={404} />
                <NotFound />
              </>
            );
          }

          if (data.issuerByTickerSymbol.__typename === "BrazilianCompanyIssuer") {
            return (
              <NavbarLayout>
                <OverdueSubscriptionStickyBanner />

                <LocaleContext.Provider value="BRA">
                  <BrazilianCompanyContext.Provider value={data.issuerByTickerSymbol}>
                    <BrazilianCompanyPage issuer={data.issuerByTickerSymbol} tickerSymbol={normalizedTickerSymbol} />
                  </BrazilianCompanyContext.Provider>
                </LocaleContext.Provider>
              </NavbarLayout>
            );
          }

          if (data.issuerByTickerSymbol.__typename === "BrazilianRealEstateFund") {
            if (user === null) {
              return (
                <FundamenteiWithTextLayout>
                  <LocaleContext.Provider value="BRA">
                    <BrazilianRealEstateFundContext.Provider value={data.issuerByTickerSymbol}>
                      <GatedBrazilianRealEstateFund realEstateFund={data.issuerByTickerSymbol} />
                    </BrazilianRealEstateFundContext.Provider>
                  </LocaleContext.Provider>
                </FundamenteiWithTextLayout>
              );
            }

            return (
              <NavbarLayout>
                <OverdueSubscriptionStickyBanner />

                <LocaleContext.Provider value="BRA">
                  <BrazilianRealEstateFundContext.Provider value={data.issuerByTickerSymbol}>
                    <BrazilianRealEstateFund
                      realEstateFund={data.issuerByTickerSymbol}
                      tickerSymbol={normalizedTickerSymbol}
                    />
                  </BrazilianRealEstateFundContext.Provider>
                </LocaleContext.Provider>
              </NavbarLayout>
            );
          }

          return (
            <>
              <SetHttpStatusCode httpStatus={404} />
              <NotFound />
            </>
          );
        }}
      </Query>
    </>
  );
};

IssuerByTickerSymbol.getInitialProps = async ({ query, req, res }) => {
  if (res && req?.headers.cookie && !parseRawCookies(req.headers.cookie).has("token")) {
    res.setHeader("cache-control", "public, max-age=43200");
    res.setHeader("x-cache-hit", "true");
    res.setHeader("etag", "");
  }
  return {
    tickerSymbol: Array.isArray(query.tickerSymbol) ? query.tickerSymbol[0] : query.tickerSymbol,
  };
};

export default IssuerByTickerSymbol;

function parseRawCookies(cookies: string) {
  return cookies.split(";").reduce<Map<string, any>>((parsed, cookie) => {
    const [key, value] = cookie.trim().split("=");
    try {
      return parsed.set(key, JSON.parse(value));
    } catch {
      return parsed.set(key, value);
    }
  }, new Map());
}
